/**
 * Schedule Quick-Create Drawer (Phase 5 Vague C)
 *
 * ESM replacement for the 744-LOC inline ``{% include %}`` that used to
 * live in ``templates/office/schedule/_shared_quick_create.html``
 * (loaded inside the bottom ``<script>`` of ``schedule_hub.html``).
 *
 * Owns the right-hand "+" drawer that lets the office create a new
 * visit in a single shot — customer-first flow:
 *
 *   Step 1: Pick a customer (autocomplete, ON_HOLD visible-but-disabled).
 *   Step 2: Choose a target — one of the customer's recurring jobs OR
 *           "+ New one-off visit".
 *   Step 3: For one-off, fill the inline mini-form (location, optional
 *           contract, pricing, optional notes) — when a contract is
 *           picked its defaults (duration, billing grouping) flow
 *           through; standalone visits keep the legacy free-mode
 *           behaviour. PR-6b (2026-05) replaced the legacy ServiceType
 *           picker with this optional Contract (WorkSite) picker.
 *   Step 4: Date / time / duration / cleaner picker, then submit.
 *
 * Submission is split between two endpoints:
 *
 *   - recurring → POST /office/api/visits/create/        (existing job)
 *   - one_off   → POST /office/api/quick-add/one-off/    (creates a fresh
 *                                                          one-off Job +
 *                                                          first visit)
 *
 * The full ``window.ScheduleHub.quickCreate.*`` API is preserved
 * verbatim — both schedule-calendar.js and the team grid module
 * (Vague D) call ``ScheduleHub.quickCreate.show(date, time, cleaner?)``
 * to open the drawer when the user clicks an empty cell.
 *
 * Wiring
 * ------
 * Auto-init via ``data-auto-init="scheduleQuickCreate"`` on the
 * ``#createVisitDrawer`` root. The 4 dismissal/submit ``onclick=`` legacy
 * attributes (drawer header X, footer Cancel, footer Create, backdrop)
 * become ``data-quick-create-action="hide|submit"`` and are routed by
 * a single delegated click listener. The ``X`` next to the customer
 * search keeps its ``id="quickCreateCustomerClear"`` selector so we
 * don't have to hunt down every Tailwind class — the click delegation
 * handles it via id-closest match.
 */

import { customerAvatarHtml } from '../utils/ui/customer-avatar.js';

let _bootstrapped = false;
let _customerSearchTimeout = null;
let _custHighlightIdx = -1;

function getQc() {
  return window.ScheduleHub && window.ScheduleHub.quickCreate;
}

function getUtils() {
  return (window.ScheduleHub && window.ScheduleHub.utils) || {};
}

function getRefresh() {
  return (window.ScheduleHub && window.ScheduleHub.refresh) || {};
}

function tr(key) {
  return (window.TRANSLATIONS && window.TRANSLATIONS[key]) || key;
}

// ============================================================
// Show / hide
// ============================================================

function show(dateStr, timeStr, preselectedCleanerId) {
  const drawer = document.getElementById('createVisitDrawer');
  const backdrop = document.getElementById('createVisitDrawerBackdrop');
  if (!drawer || !backdrop) return;

  // Convert YYYY-MM-DD → dd/mm/yyyy for Flowbite datepicker
  let formattedDate = '';
  if (dateStr && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const [y, m, d] = dateStr.split('T')[0].split('-');
    formattedDate = `${d}/${m}/${y}`;
  } else if (dateStr && dateStr.includes('-')) {
    const parts = dateStr.split('-');
    if (parts.length === 3) formattedDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  reset();

  const dateEl = document.getElementById('id_scheduled_date');
  const timeEl = document.getElementById('id_scheduled_time');
  if (dateEl) dateEl.value = formattedDate;
  if (timeEl) timeEl.value = timeStr || '';

  backdrop.classList.remove('hidden');
  drawer.classList.remove('translate-x-full');
  document.body.classList.add('overflow-hidden');

  if (preselectedCleanerId) {
    const pickerEl = document.getElementById('quick-create-picker');
    if (pickerEl) {
      const isObj =
        typeof preselectedCleanerId === 'object' && preselectedCleanerId.id;
      const payload = isObj
        ? [{
            id: String(preselectedCleanerId.id),
            name: preselectedCleanerId.name,
            photoUrl: preselectedCleanerId.photoUrl || '',
            initials: preselectedCleanerId.initials || '',
          }]
        : [String(preselectedCleanerId)];

      const doSelect = () => {
        let attempts = 0;
        const trySelect = () => {
          attempts++;
          if (pickerEl.cleanerPicker && pickerEl.cleanerPicker.setSelected) {
            pickerEl.cleanerPicker.setSelected(payload);
          } else if (attempts < 10) {
            setTimeout(trySelect, 50);
          }
        };
        setTimeout(trySelect, 100);
      };

      // The team lane click pre-fills whichever cleaner row was clicked —
      // even one who is absent that day. Skip the pre-fill (and explain via
      // a toast) when the cleaner is off on the chosen date, so the operator
      // starts from an empty picker instead of a booking the server will
      // reject. Falls back to pre-selecting if we can't determine absence.
      if (isObj && formattedDate) {
        skipPreselectIfAbsent(preselectedCleanerId, formattedDate, timeStr, doSelect);
      } else {
        doSelect();
      }
    }
  }

  setTimeout(() => document.getElementById('quickCreateCustomerSearch')?.focus(), 120);
}

// Probe the cleaner-search availability endpoint for the clicked cleaner on
// the chosen date. When absent → toast + leave the picker empty; otherwise
// invoke ``onAvailable`` to pre-select as usual. Date-only is enough (the
// endpoint flags full-day absences without a time).
async function skipPreselectIfAbsent(cleaner, dateStr, timeStr, onAvailable) {
  try {
    const token = (cleaner.name || '').trim().split(/\s+/)[0] || '';
    let url =
      '/office/api/cleaners/search/?limit=50&q=' +
      encodeURIComponent(token) +
      '&date=' +
      encodeURIComponent(dateStr);
    if (timeStr) url += '&time=' + encodeURIComponent(timeStr);
    const resp = await fetch(url);
    const data = await resp.json();
    const match = (data.results || []).find(
      (r) => String(r.id) === String(cleaner.id),
    );
    if (match && match.is_absent) {
      if (typeof window.showToast === 'function') {
        window.showToast(
          tr('cleanerAbsentSkipped').replace('%(name)s', cleaner.name || ''),
          'warning',
        );
      }
      return;
    }
  } catch {
    // Network / parse failure — fall through and pre-select (safe default;
    // the server-side guard still blocks an absent booking on submit).
  }
  onAvailable();
}

function hide() {
  const drawer = document.getElementById('createVisitDrawer');
  const backdrop = document.getElementById('createVisitDrawerBackdrop');
  if (!drawer || !backdrop) return;
  drawer.classList.add('translate-x-full');
  backdrop.classList.add('hidden');
  document.body.classList.remove('overflow-hidden');
}

// ============================================================
// Reset every field
// ============================================================

function reset() {
  // Customer
  const custInput = document.getElementById('quickCreateCustomerSearch');
  const custIdInput = document.getElementById('quickCreateCustomerId');
  const custResults = document.getElementById('quickCreateCustomerResults');
  if (custInput) custInput.value = '';
  if (custIdInput) custIdInput.value = '';
  if (custResults) { custResults.classList.add('hidden'); custResults.innerHTML = ''; }
  document.getElementById('quickCreateCustomerClear')?.classList.add('hidden');

  // Mode + job target
  const modeInput = document.getElementById('quickCreateMode');
  const jobIdInput = document.getElementById('quickCreateJobId');
  const jobSection = document.getElementById('quickCreateJobSection');
  const jobList = document.getElementById('quickCreateJobList');
  if (modeInput) modeInput.value = '';
  if (jobIdInput) jobIdInput.value = '';
  if (jobSection) jobSection.classList.add('hidden');
  if (jobList) jobList.innerHTML = '';

  // One-off mini-form
  const oneOffForm = document.getElementById('quickCreateOneOffForm');
  if (oneOffForm) oneOffForm.classList.add('hidden');
  const locSel = document.getElementById('quickCreateLocation');
  if (locSel) locSel.innerHTML = '';
  // Reset the category picker so a fresh open of the drawer doesn't
  // carry over the previous customer's category catalogue (a different
  // tenant's customer might have a different list of active categories).
  const catSel = document.getElementById('quickCreateCategory');
  if (catSel) catSel.innerHTML = '';
  const pm = document.getElementById('quickCreatePricingModel');
  if (pm) {
    // PR-6c-5 (2026-05) — reset to ``flat_per_visit`` after the
    // legacy ``fixed`` mode was dropped server-side.
    pm.value = 'flat_per_visit';
    // The custom-select wrapper mirrors the underlying <select> only on
    // 'change', so a programmatic ``.value =`` needs an explicit dispatch.
    pm.dispatchEvent(new Event('change', { bubbles: true }));
  }
  const price = document.getElementById('quickCreatePrice');
  if (price) price.value = '';
  const priceLabel = document.getElementById('quickCreatePriceLabel');
  if (priceLabel) priceLabel.innerHTML =
    `${tr('priceFixedLabel')} <span class="text-red-500">*</span>`;
  const notes = document.getElementById('quickCreateNotes');
  if (notes) notes.value = '';
  const mtdEl = document.getElementById('quickCreateMobileTrackingDisabled');
  if (mtdEl) mtdEl.checked = false;

  const pricingBlock = document.getElementById('quickCreatePricingBlock');
  const durationField = document.getElementById('quickCreateDurationField');
  const overrideField = document.getElementById('quickCreateBillingOverrideField');
  const overrideInput = document.getElementById('quickCreateBillingModeOverride');
  if (pricingBlock) pricingBlock.classList.remove('hidden');
  if (durationField) durationField.classList.remove('hidden');
  if (overrideField) overrideField.classList.add('hidden');
  if (overrideInput) overrideInput.value = '';

  // Date / time
  ['id_scheduled_date', 'id_scheduled_time']
    .forEach((id) => { const el = document.getElementById(id); if (el) el.value = ''; });
  // Duration defaults to 120 (a real, editable value — not a greyed
  // placeholder) for the one-off form. Selecting an existing job
  // overwrites it from the radio's data-duration further down.
  const durEl = document.getElementById('id_scheduled_duration_minutes');
  if (durEl) durEl.value = '120';

  // Cleaner picker
  const pickerEl = document.getElementById('quick-create-picker');
  if (pickerEl && pickerEl.cleanerPicker) pickerEl.cleanerPicker.setSelected([]);
  // Slice F.15 — clear any leftover externally-handled lock so a fresh
  // open of the drawer doesn't carry over the previous job's state.
  applyExternallyHandledLock(false);

  // Errors
  [
    'quickCreateCustomerError', 'quickCreateJobError',
    'quickCreateLocationError',
    'quickCreateCategoryError',
    'quickCreatePriceError',
    'quickCreateDateError', 'quickCreateTimeError',
    'quickCreateCleanerError', 'quickCreateGeneralError',
  ].forEach((id) => document.getElementById(id)?.classList.add('hidden'));

  const qc = getQc();
  if (qc) qc._customerContext = null;
}

// ============================================================
// Customer search + selection
// ============================================================

function toggleCustomerClearButton() {
  const input = document.getElementById('quickCreateCustomerSearch');
  const btn = document.getElementById('quickCreateCustomerClear');
  if (!input || !btn) return;
  btn.classList.toggle('hidden', !input.value);
}

function clearCustomerSelection() {
  const input = document.getElementById('quickCreateCustomerSearch');
  const custIdInput = document.getElementById('quickCreateCustomerId');
  const results = document.getElementById('quickCreateCustomerResults');
  const jobSection = document.getElementById('quickCreateJobSection');
  const jobList = document.getElementById('quickCreateJobList');
  const oneOffForm = document.getElementById('quickCreateOneOffForm');
  const overrideField = document.getElementById('quickCreateBillingOverrideField');
  const overrideInput = document.getElementById('quickCreateBillingModeOverride');
  const modeInput = document.getElementById('quickCreateMode');
  const jobIdInput = document.getElementById('quickCreateJobId');

  if (input) input.value = '';
  if (custIdInput) custIdInput.value = '';
  if (results) { results.classList.add('hidden'); results.innerHTML = ''; }
  if (jobSection) jobSection.classList.add('hidden');
  if (jobList) jobList.innerHTML = '';
  if (oneOffForm) oneOffForm.classList.add('hidden');
  if (overrideField) overrideField.classList.add('hidden');
  if (overrideInput) overrideInput.value = '';
  if (modeInput) modeInput.value = '';
  if (jobIdInput) jobIdInput.value = '';
  const qc = getQc();
  if (qc) qc._customerContext = null;

  toggleCustomerClearButton();
  if (input) input.focus();
}

async function searchCustomers(query) {
  const resultsContainer = document.getElementById('quickCreateCustomerResults');
  if (!resultsContainer) return;
  const utils = getUtils();
  try {
    // Quick-create is a *creation* picker → ON_HOLD customers must be
    // visible (so the office understands why a familiar name does not
    // produce selectable jobs) but blocked from selection. INACTIVE
    // customers stay out: no business path leads to a new visit for an
    // ended service. Server-side, the per-job context endpoint
    // (``api_quick_add_customer_context``) and the visit create endpoint
    // both filter to status=ACTIVE — UI just makes the gate transparent.
    const resp = await fetch(`/office/api/customers/search/?q=${encodeURIComponent(query)}&include=active,on_hold`);
    const data = await resp.json();
    if (data.results && data.results.length > 0) {
      const i18n = window.CUSTOMER_PICKER_I18N || {};
      resultsContainer.innerHTML = data.results.map((c) => {
        const onHold = c.status === 'on_hold';
        const rowClasses = onHold
          ? 'px-3 py-2 cursor-default opacity-60'
          : 'px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer';
        const disabledAttr = onHold ? ' data-disabled="true"' : '';
        const badge = onHold
          ? ` <span class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">${utils.escapeHtml(i18n.on_hold || 'On hold')}</span>`
          : '';
        return `
          <div class="${rowClasses} flex items-center"
               data-customer-id="${c.id}"
               data-customer-name="${utils.escapeJsAttr(c.name)}"${disabledAttr}>
              ${customerAvatarHtml(c.name, c.customer_type)}
              <div class="text-sm font-medium text-gray-900 dark:text-white inline-flex items-center">${utils.escapeHtml(c.name)}${badge}</div>
          </div>`;
      }).join('');
      resultsContainer.classList.remove('hidden');
      // Per-row click handlers stay direct (lighter than delegation
      // for a list rebuilt on every keystroke).
      resultsContainer.querySelectorAll('[data-customer-id]').forEach((el) => {
        if (el.dataset.disabled === 'true') return;
        el.addEventListener('click', () => {
          selectCustomer(parseInt(el.dataset.customerId, 10), el.dataset.customerName);
        });
      });
    } else {
      resultsContainer.innerHTML = `
        <div class="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">
            ${tr('noCustomersMatching')}
        </div>`;
      resultsContainer.classList.remove('hidden');
    }
  } catch (err) {
    console.error('Customer search error:', err);
    resultsContainer.innerHTML = `
      <div class="px-3 py-2 text-red-600 dark:text-red-400 text-sm">
          ${tr('errorCreatingVisit')}
      </div>`;
    resultsContainer.classList.remove('hidden');
  }
}

async function selectCustomer(customerId, customerName) {
  const custInput = document.getElementById('quickCreateCustomerSearch');
  const custIdInput = document.getElementById('quickCreateCustomerId');
  const resultsContainer = document.getElementById('quickCreateCustomerResults');
  const jobSection = document.getElementById('quickCreateJobSection');
  const jobList = document.getElementById('quickCreateJobList');

  if (custInput) custInput.value = customerName;
  if (custIdInput) custIdInput.value = String(customerId);
  if (resultsContainer) resultsContainer.classList.add('hidden');
  document.getElementById('quickCreateCustomerError')?.classList.add('hidden');
  toggleCustomerClearButton();

  if (jobSection) jobSection.classList.remove('hidden');
  if (jobList) {
    jobList.innerHTML = `
      <div class="text-sm text-gray-500 dark:text-gray-400 p-2">
          ${tr('loadingCustomer')}
      </div>`;
  }

  try {
    const resp = await fetch(`/office/api/quick-add/customer/${customerId}/context/`);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const ctx = await resp.json();
    const qc = getQc();
    if (qc) qc._customerContext = ctx;
    renderJobOptions(ctx);
  } catch (err) {
    console.error('Customer context load failed:', err);
    if (jobList) {
      jobList.innerHTML = `
        <div class="text-sm text-red-600 dark:text-red-400 p-2">
            ${tr('errorCreatingVisit')}
        </div>`;
    }
  }
}

// ============================================================
// Job target list (recurring jobs + one-off radio)
// ============================================================

function renderJobOptions(ctx) {
  const jobList = document.getElementById('quickCreateJobList');
  if (!jobList) return;
  const utils = getUtils();
  const esc = (utils.escapeHtml) || ((t) => t);
  const parts = [];

  if (ctx.recurring_jobs && ctx.recurring_jobs.length > 0) {
    for (const job of ctx.recurring_jobs) {
      // Slice F.15 — surface the externally-handled flag so the cleaner
      // picker can be silently neutralised when the user picks one of
      // these jobs. The server also rejects ``cleaner_ids`` POSTed for
      // such visits with an explicit message, but blocking upfront in
      // the UI is cleaner than letting the user fill the picker only to
      // see a generic toast on submit.
      const externallyHandled = job.is_externally_handled === true;
      const externallyHandledHint = externallyHandled
        ? ` <span class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">${tr('externallyHandled') || 'externally handled'}</span>`
        : '';
      parts.push(`
        <label class="qc-job-option flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 dark:border-gray-600 p-3 hover:border-primary-500 transition-colors">
          <input type="radio" name="qc_target" value="recurring:${job.id}"
                 data-mode="recurring"
                 data-job-id="${job.id}"
                 data-duration="${job.default_duration}"
                 data-externally-handled="${externallyHandled ? '1' : '0'}"
                 class="mt-1 w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600">
          <div class="flex-1">
            <div class="font-medium text-gray-900 dark:text-white">
              ${esc(job.job_label)} · ${esc(job.location)}${externallyHandledHint}
            </div>
            <div class="text-helper">
              ${esc(job.recurrence_label)} · ${tr('recurringLabel')}
            </div>
          </div>
        </label>`);
    }
  } else {
    parts.push(`
      <div class="text-sm text-gray-500 dark:text-gray-400 italic px-1">
        ${tr('noRecurringForCustomer')}
      </div>`);
  }

  parts.push(`
    <label class="qc-job-option flex items-start gap-3 cursor-pointer rounded-lg border-2 border-dashed border-primary-300 dark:border-primary-600 bg-primary-50/40 dark:bg-primary-900/10 p-3 hover:border-primary-500 transition-colors">
      <input type="radio" name="qc_target" value="one_off"
             data-mode="one_off"
             class="mt-1 w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600">
      <div class="flex-1">
        <div class="font-medium text-primary-700 dark:text-primary-300">
          ${tr('newOneOffVisit')}
        </div>
        <div class="text-helper">${tr('newOneOffHint')}</div>
      </div>
    </label>`);

  jobList.innerHTML = parts.join('');

  jobList.querySelectorAll('input[name="qc_target"]').forEach((radio) => {
    radio.addEventListener('change', () => onTargetChange(radio));
  });

  // Always default to "+ New one-off visit" (the last radio, appended
  // above) so the operator lands directly in the one-off mini-form.
  // Attaching the visit to an existing recurring mission stays one
  // click away via the radios listed before it.
  const radios = jobList.querySelectorAll('input[name="qc_target"]');
  if (radios.length > 0) {
    const oneOffRadio = radios[radios.length - 1];
    oneOffRadio.checked = true;
    onTargetChange(oneOffRadio);
  }
}

function onTargetChange(radio) {
  const mode = radio.dataset.mode;
  const modeInput = document.getElementById('quickCreateMode');
  const jobIdInput = document.getElementById('quickCreateJobId');
  const oneOffForm = document.getElementById('quickCreateOneOffForm');
  const durationInput = document.getElementById('id_scheduled_duration_minutes');

  document.getElementById('quickCreateJobError')?.classList.add('hidden');

  if (mode === 'recurring') {
    if (modeInput) modeInput.value = 'recurring';
    if (jobIdInput) jobIdInput.value = radio.dataset.jobId;
    if (oneOffForm) oneOffForm.classList.add('hidden');
    if (durationInput && radio.dataset.duration) durationInput.value = radio.dataset.duration;
    applyExternallyHandledLock(radio.dataset.externallyHandled === '1');
  } else if (mode === 'one_off') {
    if (modeInput) modeInput.value = 'one_off';
    if (jobIdInput) jobIdInput.value = '';
    if (oneOffForm) oneOffForm.classList.remove('hidden');
    populateOneOffForm();
    // One-off creation never inherits an externally-handled WorkSite
    // from the picker (the user fills the customer/location/service
    // type themselves and the new Job has no work_site by default).
    applyExternallyHandledLock(false);
  }
}

/**
 * Slice F.15 — neutralise the cleaner picker when the picked recurring
 * Job is externally handled (CIaO scope + 100 % USES_OWN_SYSTEM
 * worksite). Wipes any selection, hides the picker UI, and renders a
 * blue informational note in its place. Server-side
 * ``api_occurrence_create`` also rejects ``cleaner_ids`` for these
 * visits so this is purely a UX-side guard against the "fill the
 * picker, get an opaque error toast" flow reported by the PO.
 */
function applyExternallyHandledLock(isExternallyHandled) {
  const pickerEl = document.getElementById('quick-create-picker');
  let noteEl = document.getElementById('quickCreatePickerExternallyHandledNote');

  if (isExternallyHandled) {
    if (pickerEl && pickerEl.cleanerPicker && pickerEl.cleanerPicker.setSelected) {
      pickerEl.cleanerPicker.setSelected([]);
    }
    if (pickerEl) pickerEl.classList.add('hidden');

    if (!noteEl && pickerEl && pickerEl.parentNode) {
      noteEl = document.createElement('div');
      noteEl.id = 'quickCreatePickerExternallyHandledNote';
      noteEl.className = 'rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 p-3 text-sm text-blue-800 dark:text-blue-300';
      pickerEl.parentNode.insertBefore(noteEl, pickerEl);
    }
    if (noteEl) {
      noteEl.textContent = tr('externallyHandledNoCleanerHint')
        || 'This job is fully delegated to a subcontractor that uses its own ONSS system. No cleaner is assigned to its visits.';
      noteEl.classList.remove('hidden');
    }
  } else {
    if (pickerEl) pickerEl.classList.remove('hidden');
    if (noteEl) noteEl.classList.add('hidden');
  }
}

// ============================================================
// One-off mini-form
// ============================================================

function populateOneOffForm() {
  const qc = getQc();
  const ctx = qc && qc._customerContext;
  if (!ctx) return;
  const utils = getUtils();

  const locSel = document.getElementById('quickCreateLocation');

  if (locSel && locSel.options.length === 0) {
    if (ctx.locations.length === 0) {
      locSel.innerHTML = `<option value="">${tr('customerHasNoLocation')}</option>`;
    } else {
      locSel.innerHTML = ctx.locations.map((l) =>
        `<option value="${l.id}">${utils.escapeHtml(l.label)}</option>`
      ).join('');
    }
  }

  // « Type de service » picker. The list is surfaced by
  // ``api_quick_add_customer_context`` as ``categories``. A « + New
  // one-off visit » is always standalone (no contract), so the
  // category is unconditionally REQUIRED here. Defensive ``if (catSel)``
  // so a legacy template build remains functional.
  const catSel = document.getElementById('quickCreateCategory');
  if (catSel && catSel.options.length === 0) {
    const categories = Array.isArray(ctx.categories) ? ctx.categories : [];
    const placeholder = tr('selectServiceType') || 'Select...';
    let html = `<option value="">${utils.escapeHtml(placeholder)}</option>`;
    for (const c of categories) {
      html += `<option value="${c.id}">${utils.escapeHtml(c.name)}</option>`;
    }
    catSel.innerHTML = html;
  }

  const pm = document.getElementById('quickCreatePricingModel');
  if (pm) {
    pm.removeEventListener('change', onPricingModelChange);
    pm.addEventListener('change', onPricingModelChange);
  }

  applyBillingOverrideMode();
}

/**
 * JOB_BILLING_REFACTOR PR-5 (2026-05) — Option A : the picker
 * for ``invoice_grouping`` is always visible (the Contract no
 * longer carries a default grouping, so the operator picks
 * PER_VISIT or MONTHLY_GROUPED on every quick-add). This kept
 * function is a no-op shim retained for back-compat with the
 * existing callers — slated for removal in a follow-up cleanup.
 */
function applyBillingOverrideMode() {
  // Picker is always shown by the template ; nothing to toggle.
}

function onPricingModelChange() {
  const pm = document.getElementById('quickCreatePricingModel');
  const priceLabel = document.getElementById('quickCreatePriceLabel');
  if (!pm || !priceLabel) return;
  const text = pm.value === 'hourly' ? tr('priceHourlyLabel') : tr('priceFixedLabel');
  priceLabel.innerHTML = `${text} <span class="text-red-500">*</span>`;
}

// ============================================================
// Submit
// ============================================================

async function submit() {
  const submitBtn = document.getElementById('quickCreateSubmitBtn');
  const utils = getUtils();

  const customerId = document.getElementById('quickCreateCustomerId').value;
  const mode = document.getElementById('quickCreateMode').value;
  const jobId = document.getElementById('quickCreateJobId').value;
  const dateRaw = document.getElementById('id_scheduled_date').value;
  const time = document.getElementById('id_scheduled_time').value;
  const duration = document.getElementById('id_scheduled_duration_minutes').value;

  let date = dateRaw;
  if (dateRaw && dateRaw.includes('/')) {
    const [d, m, y] = dateRaw.split('/');
    date = `${y}-${m}-${d}`;
  }

  const pickerEl = document.getElementById('quick-create-picker');
  const hiddenField = pickerEl?.querySelector('.hidden-select');
  let selectedCleanerIds = [];
  if (hiddenField) {
    if (hiddenField.tagName === 'SELECT') {
      selectedCleanerIds = Array.from(hiddenField.selectedOptions)
        .filter((opt) => opt.value)
        .map((opt) => parseInt(opt.value, 10));
    } else if (hiddenField.value) {
      selectedCleanerIds = [parseInt(hiddenField.value, 10)];
    }
  }

  const errorEls = {
    customer: document.getElementById('quickCreateCustomerError'),
    job: document.getElementById('quickCreateJobError'),
    location: document.getElementById('quickCreateLocationError'),
    price: document.getElementById('quickCreatePriceError'),
    date: document.getElementById('quickCreateDateError'),
    time: document.getElementById('quickCreateTimeError'),
    cleaner: document.getElementById('quickCreateCleanerError'),
    general: document.getElementById('quickCreateGeneralError'),
  };
  Object.values(errorEls).forEach((el) => el?.classList.add('hidden'));

  let hasError = false;
  const setError = (el, msg) => {
    if (!el) return;
    el.textContent = msg;
    el.classList.remove('hidden');
    hasError = true;
  };

  if (!customerId) setError(errorEls.customer, tr('pleaseSelectCustomer'));
  if (!mode) setError(errorEls.job, tr('pleaseSelectOption'));
  if (!dateRaw) setError(errorEls.date, tr('pleaseSelectDate'));
  if (!time) setError(errorEls.time, tr('pleaseSelectTime'));

  let oneOffPayload = null;
  if (mode === 'one_off') {
    const locationId = document.getElementById('quickCreateLocation').value;
    // « + New one-off visit » is always a standalone ad-hoc visit:
    // no contract is ever attached from this surface (attaching to a
    // contract is done by picking an existing mission in the radio).
    const pricingModel = document.getElementById('quickCreatePricingModel').value;
    // The price field is a plain text input (``inputmode=decimal``) like
    // the mission form's ``DecimalTextInput`` — ``type=number`` silently
    // wiped the field when the typed separator did not match the browser
    // locale. Normalise the comma to a dot here so the API always cleans
    // a dot-form decimal, regardless of locale or paste.
    const priceRaw = document.getElementById('quickCreatePrice').value.trim();
    const price = priceRaw.replace(',', '.');
    const notes = document.getElementById('quickCreateNotes').value;
    const mtdEl = document.getElementById('quickCreateMobileTrackingDisabled');
    const mobileTrackingDisabled = !!(mtdEl && mtdEl.checked);

    // « Type de service » picker. A new one-off is always standalone
    // (no contract), so the category is unconditionally REQUIRED. The
    // drawer template renders ``#quickCreateCategory`` only when the
    // catalogue is non-empty ; missing element = legacy build →
    // silently skipped, server-side enforcement still applies.
    const categoryEl = document.getElementById('quickCreateCategory');
    const categoryId = categoryEl ? categoryEl.value : '';

    if (!locationId) setError(errorEls.location, tr('pleaseSelectLocation'));

    const overrideInputEl = document.getElementById('quickCreateBillingModeOverride');
    const billingOverride = (overrideInputEl && overrideInputEl.value) || '';

    if (!priceRaw) {
      setError(errorEls.price, tr('pleasePriceRequired'));
    } else {
      // Strictly positive, mirroring the mission form / server rule.
      const priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum <= 0) {
        setError(errorEls.price, pricingModel === 'hourly'
          ? tr('priceHourlyGreaterThanZero')
          : tr('pricePerVisitGreaterThanZero'));
      }
    }

    // Client-side mirror of the server validation rule: a standalone
    // visit (always, now) requires a service type.
    if (categoryEl && !categoryId) {
      const catErr = document.getElementById('quickCreateCategoryError');
      if (catErr) {
        catErr.textContent = tr('pleaseSelectCategory') || 'Please select a service type';
        catErr.classList.remove('hidden');
      }
      hasError = true;
    }

    oneOffPayload = {
      customer_id: parseInt(customerId, 10),
      location_id: parseInt(locationId, 10),
      // Always standalone — never attach a contract from this surface.
      work_site_id: null,
      category_id: categoryId ? parseInt(categoryId, 10) : null,
      date: date,
      time: time,
      duration_minutes: duration ? parseInt(duration, 10) : null,
      pricing_model: pricingModel,
      // PR-6c-5 (2026-05) — the ``fixed`` mode was dropped ; the
      // backend now expects ``per_visit_price`` paired with
      // ``flat_per_visit`` (it still accepts ``fixed_price`` as an
      // alias for one release of overlap with this JS, see
      // ``apps/office/views/api.py::api_quick_add_one_off_create``).
      per_visit_price: pricingModel === 'flat_per_visit' ? price : null,
      hourly_rate: pricingModel === 'hourly' ? price : null,
      invoice_grouping: billingOverride || null,
      mobile_tracking_disabled: mobileTrackingDisabled,
      cleaner_ids: selectedCleanerIds,
      notes: notes,
    };
  } else if (mode === 'recurring' && !jobId) {
    setError(errorEls.job, tr('pleaseSelectOption'));
  }

  if (hasError) return;

  submitBtn.disabled = true;
  const originalContent = submitBtn.innerHTML;
  submitBtn.innerHTML =
    '<svg class="animate-spin h-4 w-4 mr-2 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">' +
    '<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>' +
    '<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>' +
    '</svg> ' + tr('creating');

  try {
    let resp;
    if (mode === 'recurring') {
      resp = await fetch('/office/api/visits/create/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': utils.getCsrfToken(),
        },
        body: JSON.stringify({
          job_id: parseInt(jobId, 10),
          date: date,
          time: time,
          duration_minutes: duration ? parseInt(duration, 10) : null,
          cleaner_ids: selectedCleanerIds,
        }),
      });
    } else {
      resp = await fetch('/office/api/quick-add/one-off/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': utils.getCsrfToken(),
        },
        body: JSON.stringify(oneOffPayload),
      });
    }
    const data = await resp.json();

    if (data.success) {
      hide();
      if (typeof window.showToast === 'function') {
        window.showToast(data.message || tr('visitCreatedSuccess'), 'success');
      }
      const refresh = getRefresh();
      refresh.calendar?.();
      refresh.team?.();
      // Office visits list registers ``refresh.list`` so a visit created
      // from the list's « New Visit » button shows up without a manual
      // reload (absent on the calendar / team boards — see visits.js).
      refresh.list?.();
    } else {
      setError(errorEls.general, data.error || tr('failedToCreateVisit'));
    }
  } catch (err) {
    console.error('Create error:', err);
    setError(errorEls.general, tr('errorCreatingVisit'));
  } finally {
    submitBtn.disabled = false;
    submitBtn.innerHTML = originalContent;
  }
}

// ============================================================
// Public init
// ============================================================

function ensureNamespace() {
  window.ScheduleHub = window.ScheduleHub || {};
  window.ScheduleHub.quickCreate = window.ScheduleHub.quickCreate || {};
}

function bindGlobalListeners() {
  if (_bootstrapped) return;
  _bootstrapped = true;

  // Customer search input → debounced API call
  document.addEventListener('input', function onCustomerInput(e) {
    if (e.target.id !== 'quickCreateCustomerSearch') return;
    clearTimeout(_customerSearchTimeout);
    const query = e.target.value.trim();
    // Typing a new query invalidates any prior selection
    const custIdInput = document.getElementById('quickCreateCustomerId');
    if (custIdInput) custIdInput.value = '';
    toggleCustomerClearButton();
    _customerSearchTimeout = setTimeout(
      () => searchCustomers(query),
      query.length === 0 ? 0 : 250,
    );
  });

  document.addEventListener('click', function onAnyClick(e) {
    // Customer search field clicked → re-open dropdown
    if (e.target.id === 'quickCreateCustomerSearch') {
      searchCustomers(e.target.value.trim());
      return;
    }

    // Customer × clear button
    const clearBtn = e.target.closest('#quickCreateCustomerClear');
    if (clearBtn) {
      e.preventDefault();
      e.stopPropagation();
      clearCustomerSelection();
      return;
    }

    // Drawer-level actions (header X, footer Cancel, footer Create, backdrop)
    const actionEl = e.target.closest('[data-quick-create-action]');
    if (actionEl) {
      const action = actionEl.dataset.quickCreateAction;
      if (action === 'hide') hide();
      else if (action === 'submit') submit();
      return;
    }
  });

  // Hide customer results when clicking outside the search input.
  document.addEventListener('click', function onClickOutsideCustomerResults(e) {
    const custSearch = document.getElementById('quickCreateCustomerSearch');
    const custResults = document.getElementById('quickCreateCustomerResults');
    if (!custSearch || !custResults) return;
    if (!custSearch.contains(e.target) && !custResults.contains(e.target)) {
      custResults.classList.add('hidden');
      _custHighlightIdx = -1;
    }
  });

  // Keyboard navigation for customer search results + Escape closes.
  document.addEventListener('keydown', function onKeydown(e) {
    const custSearch = document.getElementById('quickCreateCustomerSearch');
    const custResults = document.getElementById('quickCreateCustomerResults');

    if (e.key === 'Escape') {
      if (custResults && !custResults.classList.contains('hidden')) {
        custResults.classList.add('hidden');
        _custHighlightIdx = -1;
        return;
      }
      const drawer = document.getElementById('createVisitDrawer');
      if (drawer && !drawer.classList.contains('translate-x-full')) {
        hide();
      }
      return;
    }

    if (!custSearch || document.activeElement !== custSearch) return;
    if (!custResults || custResults.classList.contains('hidden')) return;

    const items = custResults.querySelectorAll('[data-customer-id]');
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      _custHighlightIdx = Math.min(_custHighlightIdx + 1, items.length - 1);
      items.forEach((el) => el.classList.remove('bg-gray-100', 'dark:bg-gray-600'));
      items[_custHighlightIdx].classList.add('bg-gray-100', 'dark:bg-gray-600');
      items[_custHighlightIdx].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      _custHighlightIdx = Math.max(_custHighlightIdx - 1, 0);
      items.forEach((el) => el.classList.remove('bg-gray-100', 'dark:bg-gray-600'));
      items[_custHighlightIdx].classList.add('bg-gray-100', 'dark:bg-gray-600');
      items[_custHighlightIdx].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (_custHighlightIdx >= 0 && items[_custHighlightIdx]) {
        items[_custHighlightIdx].click();
        _custHighlightIdx = -1;
      }
    }
  });
}

export function initScheduleQuickCreate() {
  ensureNamespace();

  // Public API — preserved verbatim from the legacy template so the
  // schedule-calendar / team grid / visit drawer / drawer-action-form
  // consumers don't have to change.
  const qc = window.ScheduleHub.quickCreate;
  qc.show = show;
  qc.hide = hide;
  qc.submit = submit;
  qc._reset = reset;
  qc._toggleCustomerClearButton = toggleCustomerClearButton;
  qc._clearCustomerSelection = clearCustomerSelection;
  qc._searchCustomers = searchCustomers;
  qc._selectCustomer = selectCustomer;
  qc._renderJobOptions = renderJobOptions;
  qc._onTargetChange = onTargetChange;
  qc._populateOneOffForm = populateOneOffForm;
  qc._applyBillingOverrideMode = applyBillingOverrideMode;
  qc._onPricingModelChange = onPricingModelChange;
  qc._customerContext = qc._customerContext || null;

  bindGlobalListeners();
}

export function autoInitScheduleQuickCreate() {
  initScheduleQuickCreate();
}
